import { takeLatest, put, call, select, all } from "redux-saga/effects";
import { matchPath } from "react-router-dom";
import BackendClient from "api/backend-client";
import { CommonApiMethods, PointScalingApiMethods, RecipeScalingApiMethods } from "api/api-methods";
import { backendUrl } from "api/url-builder";
import { DEFAULT_ROUTE, isRegisteredRoute } from "routes";
import RoutePaths from "routes/route-paths";
import logger from "utils/logger";
import { checkForbidden, waitForLogin } from "../auth/auth.saga";
import { login } from "../auth/auth.actions";
import { DictionaryParametersTypes, GlobalActionType, initFailure, initSuccessful, loadDecisionTreeFailure, loadDecisionTreeSuccessful, loadParametersFailure, loadParametersSuccessful, loadBioreactorsSuccessful, loadBioreactorsFailure, loadMetricTemplatesSuccessful, loadMetricTemplatesFailure, setAppVersions, loadPsTagsSuccessful, loadRsTagsSuccessful, loadPsTagsFailure, loadRsTagsFailure, setOffline, loadBioreactorParametersSuccessful, loadBioreactorParametersFailure, loadProcessVariablesSuccessful, loadProcessVariablesFailure, loadParameterTypesSuccessful, loadParameterTypesFailure, } from "./global.actions";
import { setWorkspaceServerError, setSocketError } from "../ps-workspace/ps-workspace.actions";
import { socketLastErrorsSelector } from "../ps-workspace/ps-workspace.selectors";
import { loadLicensesListSaga } from "../licensing/licensing.saga";
import { checkServerAvailable } from "utils/network";
import { isOfflineSelector } from "./global.selectors";
import { setServerError } from "store/knowledge-base-item/knowledge-base-item.actions";
import { currentLocationSelector } from "store/router/router.selectors";
import { navigate } from "shared/navigation";
var TagListType;
(function (TagListType) {
    TagListType["PS"] = "ps";
    TagListType["RS"] = "rs";
})(TagListType || (TagListType = {}));
function* initSaga() {
    logger.info(`BACKEND URL: ${backendUrl}`);
    try {
        yield call([BackendClient.getInstance(), "init"]);
        yield put(login());
        const { pathname } = window.location;
        if (!isRegisteredRoute(pathname) || matchPath(pathname, RoutePaths.Unauthorized)) {
            yield call(navigate, DEFAULT_ROUTE, null, true);
        }
        yield call(loadInitialData, initSuccessful);
    }
    catch (e) {
        yield put(initFailure());
        logger.error("init of application is failed");
    }
}
export function* checkConnectionSaga() {
    try {
        const isOfflineInStore = yield select(isOfflineSelector);
        const serverAvailable = yield call(checkServerAvailable);
        const isOfflineCurrent = !serverAvailable;
        if (isOfflineInStore !== isOfflineCurrent) {
            yield put(setOffline(isOfflineCurrent));
        }
    }
    catch (e) {
        logger.error("connection check is failed");
    }
}
export function* loadVersionsSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, CommonApiMethods.GetVersion]) || [];
        yield put(setAppVersions(data));
    }
    catch (e) {
        logger.error("version load failed");
        throw e;
    }
}
export function* loadInitialData(cb) {
    try {
        yield all([call(loadVersionsSaga), call(loadLicensesListSaga)]);
        const { pathname } = yield select(currentLocationSelector);
        const isHome = pathname === "/";
        const isRecipeScaling = pathname.startsWith("/recipe-scaling");
        if (!isHome) {
            yield call(isRecipeScaling ? loadRsInitialDataSaga : loadPsInitialDataSaga);
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "load initial data is failed");
    }
    finally {
        yield put(cb());
    }
}
function* loadPsInitialDataSaga() {
    try {
        yield all([
            call(loadDecisionTreeSaga),
            call(loadPointScalingBioreactors),
            call(loadDictionaryParameters),
            call(loadMetricTemplates),
            call(loadPsTagsSaga),
        ]);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "load point scaling initial data is failed");
    }
}
function* loadRsInitialDataSaga() {
    try {
        yield all([call(loadRecipeScalingBioreactors)]);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "load recipe scaling initial data is failed");
    }
}
function* loadDecisionTreeSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, CommonApiMethods.GetDecisionTree]);
        yield put(loadDecisionTreeSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "load decision tree is failed");
        yield put(loadDecisionTreeFailure());
        throw e;
    }
}
function* goHomeSaga() {
    try {
        yield put(setWorkspaceServerError(null));
        navigate(RoutePaths.Home, null, true);
    }
    catch (e) {
        logger.error("Navigation issue");
    }
}
function* loadDictionaryParameters() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield all(Object.values(DictionaryParametersTypes).map((type) => call([backendClient, CommonApiMethods.GetParameters], type)));
        for (const parameters of data) {
            const { data, type } = parameters;
            yield put(loadParametersSuccessful({ type, data }));
        }
    }
    catch (e) {
        yield put(loadParametersFailure());
        logger.error("Loading parameters failed");
        throw e;
    }
}
function* loadMetricTemplates() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const [simpleTemplates, relativeTemplates] = yield all([
            call([backendClient, CommonApiMethods.GetSimpleMetricTemplates]),
            call([backendClient, CommonApiMethods.GetRelativeMetricTemplates]),
        ]);
        yield put(loadMetricTemplatesSuccessful([
            ...simpleTemplates.map((template) => ({ ...template, type: "simple" })),
            ...relativeTemplates.map((template) => ({ ...template, type: "relative" })),
        ]));
    }
    catch (e) {
        yield put(loadMetricTemplatesFailure());
        logger.error("Loading templates failed");
        throw e;
    }
}
function* loadPointScalingBioreactors() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, PointScalingApiMethods.GetPsBioreactors]);
        yield put(loadBioreactorsSuccessful(data));
    }
    catch (e) {
        yield put(loadBioreactorsFailure());
        logger.error("Loading bioreactors failed");
        throw e;
    }
}
function* loadRecipeScalingBioreactors() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([
            backendClient,
            RecipeScalingApiMethods.GetRsBioreactors,
        ]);
        yield put(loadBioreactorsSuccessful(data));
    }
    catch (e) {
        yield put(loadBioreactorsFailure());
        logger.error("Loading bioreactors failed");
        throw e;
    }
}
function* getBioreactorParametersSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([
            backendClient,
            RecipeScalingApiMethods.GetBioreactorParameters,
        ]);
        yield put(loadBioreactorParametersSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "get bioreactor parameters failed");
        yield put(loadBioreactorParametersFailure());
    }
}
export function* loadProcessVariablesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const list = yield call([
            backendClient,
            RecipeScalingApiMethods.GetProcessVariables,
        ]);
        const modifiedList = list.map(({ variables, ...rest }) => ({
            ...rest,
            variablesList: variables,
        }));
        yield put(loadProcessVariablesSuccessful(modifiedList));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "process variables list load failed");
        yield put(loadProcessVariablesFailure());
    }
}
export function* loadRecipeParameterTypesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const list = yield call([
            backendClient,
            RecipeScalingApiMethods.GetRecipeParameterTypes,
        ]);
        yield put(loadParameterTypesSuccessful(list));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "parameter types list load failed");
        yield put(loadParameterTypesFailure());
    }
}
function* loadTagList(type) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const method = type === TagListType.PS ? PointScalingApiMethods.GetPsTags : RecipeScalingApiMethods.GetRsTags;
        const data = yield call([backendClient, method]);
        yield put(type === TagListType.PS ? loadPsTagsSuccessful(data) : loadRsTagsSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, `Loading ${type} tags failed`);
        yield put(type === TagListType.PS ? loadPsTagsFailure() : loadRsTagsFailure());
        throw e;
    }
}
function* loadPsTagsSaga() {
    yield call(loadTagList, TagListType.PS);
}
function* loadRsTagsSaga() {
    yield call(loadTagList, TagListType.RS);
}
function* setRenamingProgressSaga({ payload }) {
    if (!payload) {
        const currentError = yield select(socketLastErrorsSelector);
        if (currentError !== null) {
            const { errors = [] } = currentError;
            if (errors[0].propertyName) {
                yield put(setSocketError(null));
            }
        }
    }
}
export function* apiErrorHandler(e, loggerMessage, elementId) {
    yield call(checkForbidden, e);
    yield put(setServerError({ error: e, ...(elementId !== undefined ? { entityId: elementId } : {}) }));
    logger.error(loggerMessage);
}
export default function* globalSaga() {
    yield takeLatest(GlobalActionType.INIT, initSaga);
    yield takeLatest(GlobalActionType.LOAD_RS_INITIAL_DATA, loadRsInitialDataSaga);
    yield takeLatest(GlobalActionType.LOAD_PS_INITIAL_DATA, loadPsInitialDataSaga);
    yield takeLatest(GlobalActionType.LOAD_DECISION_TREE, loadDecisionTreeSaga);
    yield takeLatest(GlobalActionType.LOAD_PS_TAGS, loadPsTagsSaga);
    yield takeLatest(GlobalActionType.LOAD_RS_TAGS, loadRsTagsSaga);
    yield takeLatest(GlobalActionType.BIOREACTORS_LOAD_PARAMETERS, getBioreactorParametersSaga);
    yield takeLatest(GlobalActionType.GO_HOME, goHomeSaga);
    yield takeLatest(GlobalActionType.SET_RENAMING_PROGRESS, setRenamingProgressSaga);
    yield takeLatest(GlobalActionType.LOAD_PROCESS_VARIABLES, loadProcessVariablesSaga);
    yield takeLatest(GlobalActionType.LOAD_PARAMETER_TYPES, loadRecipeParameterTypesSaga);
}
